import { RestClient, RequestResponse } from "./base/rest.client";
import { CommonDto } from "../common.dto";
import { BASE_URL } from "../config";
import {
  CalculatePriceResponseSchema,
  ServiceCalculateResponseSchema,
  ServiceListResponseSchema,
  ServiceMetadata,
} from "./service.schemas";
import * as z from "zod";

export interface CalculatePriceRequest {
  service: {
    service_type: string;
    files: Array<{
      file_id: string;
      copies: number;
      pages: string[] | "all";
      parameters: {
        color: string | null;
        duplex: boolean;
        format: string;
      };
      total_pages: number;
    }>;
  };
  machine_id: number;
}

export interface FilePrice {
  file_id: string;
  total_price: number;
}

export interface CalculatePriceResponse {
  files: FilePrice[];
}

export interface FilePriceDetail {
  file_id: string | number;
  pages: number;
  total_pages: number;
  copies: number;
  price_per_unit: number;
  total_price: number;
  parameters?: Record<string, any> | null;
}

export interface ServiceCalculateResponse {
  total_price: number;
  files: FilePriceDetail[];
  resources_sufficient: boolean;
  missing_resources: string[];
  resources_details: string;
}

class ServiceClientClass extends RestClient {
  baseUrl = BASE_URL;

  async getServices(): Promise<{ data: { services: ServiceMetadata[] } }> {
    const result = await this.request("GET", "/api/front/services");
    const validated = this.validateResponse(result, CommonDto.withStatus(ServiceListResponseSchema));
    return { data: validated.data };
  }

  async getServiceVariants(parentServiceType: string): Promise<{ data: { services: ServiceMetadata[] } }> {
    const result = await this.request(
      "GET",
      `/api/front/services/variants?parent=${encodeURIComponent(parentServiceType)}`
    );
    const validated = this.validateResponse(result, CommonDto.withStatus(ServiceListResponseSchema));
    return { data: validated.data };
  }

  /**
   * Рассчитывает цену для услуги (старая ручка /api/services/price/calculate)
   */
  async getServiceCalculatePrice(
    data: CalculatePriceRequest
  ): Promise<{ data: CalculatePriceResponse }> {
    const result = await this.request(
      "POST",
      "/api/services/price/calculate",
      {
        jsonPayload: data,
      }
    );

    const validated = this.validateResponse(result, CommonDto.withStatus(CalculatePriceResponseSchema));

    // Возвращаем в формате, совместимом со старым API
    return {
      data: validated.data,
    };
  }

  /**
   * Рассчитывает цену для услуги с проверкой ресурсов и промокодов (новая ручка /api/services/calculate)
   */
  async getServiceCalculatePriceWithValidation(
    data: CalculatePriceRequest
  ): Promise<{ data: ServiceCalculateResponse }> {
    const result = await this.request(
      "POST",
      "/api/services/calculate",
      {
        jsonPayload: data,
      }
    );

    const validated = this.validateResponse(result, CommonDto.withStatus(ServiceCalculateResponseSchema));

    return {
      data: validated.data,
    };
  }
}

export class ServiceClient extends ServiceClientClass {}
